import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFlag } from 'flags';
import { Formik, Form, FieldArray } from 'formik';

import RuleSetCard from './components/RuleSetCard';
import AndConnector from './components/AndConnector';
import AddSetButton from './components/AddSetButton';
import LogicGuide from './components/LogicGuide';

import { setAudienceValidationSchema } from 'helpers/audiencesHelpers';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import {
  useGetAudienceByIdQuery,
  useCreateAudienceMutation,
  useUpdateAudienceMutation,
} from 'modules/audiences/audienceApi';

import * as audienceHelpers from 'helpers/audiencesHelpers';

import './NewAudience.scss';

const includes = 'incParams';
const excludes = 'excParams';

const schema = setAudienceValidationSchema();

const NewAudience = (props) => {
  const { audienceId, onModalClose, onUpdateAudience } = props;

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const isReadOnly = useFlag(['features', 'isReadOnly']);

  const id = audienceId !== undefined ? audienceId : params.id;
  const isEditing = id ? true : false;

  const [createAudience] = useCreateAudienceMutation();
  const [updateAudience] = useUpdateAudienceMutation();

  const [initialForm, setInitialForm] = useState(
    audienceHelpers.setInitialAudienceForm()
  );
  const [isGuideHidden, setIsGuideHidden] = useState(true);
  const [isExclusionExpanded, setIsExclusionExpanded] = useState(false);

  const sessionUser = useSelector((state) => state.app.user);

  const {
    data: audienceData,
    error,
    isLoading,
  } = useGetAudienceByIdQuery(id, {
    skip: !id,
  });

  const title = isEditing ? 'Update Audience' : 'Create Audience';
  const pathName = location.pathname.split('/')[1];
  const isModal = pathName === 'campaigns';

  useEffect(() => {
    if (
      !id &&
      initialForm.incParams.length === 0 &&
      initialForm.excParams.length === 0
    ) {
      setInitialForm({
        ...initialForm,
        incParams: [
          {
            logical_op_type: true,
            rules: [
              {
                id: null,
                name: 'Custom Key',
                key: '',
                kv_relational_op: 'eq',
                sort: null,
                value: '',
                value_type: 'str',
              },
            ],
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAudience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceData]);

  const loadAudience = () => {
    if (audienceData && isEditing) {
      const { incParamSets, excParamSets } = audienceHelpers.loadAudience({
        audience: audienceData,
      });

      // Auto-expand exclusion section if there are exclusion rules
      if (excParamSets.length > 0) {
        setIsExclusionExpanded(true);
      }

      setInitialForm({
        ...initialForm,
        ...audienceData,
        type: audienceData.type || 'Data Params',
        name: audienceData.name,
        incParams:
          incParamSets.length === 0 ? initialForm.incParams : incParamSets,
        excParams:
          excParamSets.length === 0 ? initialForm.excParams : excParamSets,
      });
    }
  };

  const buildAudience = (values) => {
    const { incParams, excParams, ...audience } = values;
    const { payload } = audienceHelpers.buildAudience({
      audience,
      excParams,
      incParams,
      company_id: sessionUser.company_id,
    });

    return payload;
  };

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);

    let audienceVals;
    try {
      audienceVals = buildAudience(values);
    } catch (err) {
      console.error('Error building audience:', err);
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to build audience data',
        })
      );
      form.setSubmitting(false);
      return;
    }

    if (isEditing) {
      try {
        const updatedAudience = await updateAudience({
          id: audienceVals.id,
          data: audienceVals,
        }).unwrap();

        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Audience updated successfully',
          })
        );
        if (isModal) {
          onModalClose();
          onUpdateAudience(updatedAudience);
        } else {
          navigate('/audiences');
        }
      } catch (err) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Failed to update audience',
          })
        );
      } finally {
        form.setSubmitting(false);
      }
    } else {
      try {
        const createdAudience = await createAudience(audienceVals).unwrap();

        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Audience created successfully',
          })
        );
        if (isModal) {
          onModalClose();
          onUpdateAudience(createdAudience);
        } else {
          navigate('/audiences');
        }
      } catch (err) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Failed to create audience',
          })
        );
      } finally {
        form.setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    if (isModal && onModalClose) {
      onModalClose();
    } else {
      navigate('/audiences');
    }
  };

  const addSet = (form, group) => {
    const { newForm } = audienceHelpers.addSet({ form, group });
    setInitialForm(newForm);
  };

  const removeSet = (form, group, index) => {
    const { newForm } = audienceHelpers.removeSet2({ form, group, index });
    setInitialForm(newForm);
  };

  if (isLoading) {
    return (
      <div className="aud-v2-overlay">
        <div className="aud-v2-modal">
          <div className="aud-v2-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch Audience',
      })
    );
  }

  return (
    <div className="aud-v2-overlay">
      <div className="aud-v2-modal">
        <Formik
          enableReinitialize
          initialValues={initialForm}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {(form) => {
            const { isSubmitting, values, setFieldValue } = form;

            return (
              <Form className="aud-v2-form">
                {/* Header */}
                <div className="aud-v2-header">
                  <div className="aud-v2-header__content">
                    <h1 className="aud-v2-header__title">{title}</h1>
                    <p className="aud-v2-header__subtitle">
                      Define who will receive your campaign based on specific criteria.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="aud-v2-header__close"
                    onClick={handleClose}
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>

                {/* Body */}
                <div className="aud-v2-body">
                  <div className={`aud-v2-grid ${isGuideHidden ? 'aud-v2-grid--full' : ''}`}>
                    {/* Main Column */}
                    <div className="aud-v2-main">
                      {/* Audience Name */}
                      <section className="aud-v2-section">
                        <label className="aud-v2-label">Audience Name</label>
                        <input
                          type="text"
                          className={`aud-v2-input ${form.errors.name && form.submitCount > 0 ? 'aud-v2-input--error' : ''}`}
                          placeholder="e.g. Summer Sale - Repeat Customers"
                          value={values.name}
                          onChange={(e) => setFieldValue('name', e.target.value)}
                          disabled={isReadOnly || isSubmitting}
                        />
                        {form.errors.name && form.submitCount > 0 && (
                          <div className="aud-v2-field-error">{form.errors.name}</div>
                        )}
                      </section>

                      {/* Inclusion Logic */}
                      <div className="aud-v2-logic-section">
                        <h2 className="aud-v2-logic-title">Inclusion Logic</h2>

                        <FieldArray name={includes}>
                          {(arrayHelpers) => (
                            <>
                              {values.incParams.map((ruleSet, setIndex) => (
                                <React.Fragment key={setIndex}>
                                  {setIndex > 0 && <AndConnector />}
                                  <RuleSetCard
                                    type="include"
                                    ruleSet={ruleSet}
                                    setIndex={setIndex}
                                    group={includes}
                                    form={form}
                                    initialForm={initialForm}
                                    setInitialForm={setInitialForm}
                                    onRemoveSet={() => removeSet(form, includes, setIndex)}
                                    canRemove={values.incParams.length > 1 || values.excParams.length > 0}
                                    isReadOnly={isReadOnly}
                                    isSubmitting={isSubmitting}
                                  />
                                </React.Fragment>
                              ))}
                              <AddSetButton
                                type="include"
                                onClick={() => addSet(form, includes)}
                                disabled={isReadOnly || isSubmitting}
                              />
                            </>
                          )}
                        </FieldArray>
                      </div>

                      {/* Exclusion Toggle or Divider */}
                      {!isExclusionExpanded ? (
                        <div className="aud-v2-exclusion-hint">
                          Do you need exclusion logic?{' '}
                          <button
                            type="button"
                            onClick={() => setIsExclusionExpanded(true)}
                            disabled={isReadOnly || isSubmitting}
                          >
                            Yes
                          </button>
                        </div>
                      ) : (
                        <hr className="aud-v2-divider" />
                      )}

                      {/* Exclusion Logic */}
                      {isExclusionExpanded && (
                        <div className="aud-v2-logic-section">
                          <div className="aud-v2-logic-header">
                            <h2 className="aud-v2-logic-title aud-v2-logic-title--exclude">
                              Exclusion Logic
                            </h2>
                            {values.excParams.length === 0 && (
                              <button
                                type="button"
                                className="aud-v2-logic-collapse"
                                onClick={() => setIsExclusionExpanded(false)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>

                          <FieldArray name={excludes}>
                            {(arrayHelpers) => (
                              <>
                                {values.excParams.map((ruleSet, setIndex) => (
                                  <React.Fragment key={setIndex}>
                                    {setIndex > 0 && <AndConnector />}
                                    <RuleSetCard
                                      type="exclude"
                                      ruleSet={ruleSet}
                                      setIndex={setIndex}
                                      group={excludes}
                                      form={form}
                                      initialForm={initialForm}
                                      setInitialForm={setInitialForm}
                                      onRemoveSet={() => removeSet(form, excludes, setIndex)}
                                      canRemove={values.excParams.length > 0}
                                      isReadOnly={isReadOnly}
                                      isSubmitting={isSubmitting}
                                    />
                                  </React.Fragment>
                                ))}
                                <AddSetButton
                                  type="exclude"
                                  onClick={() => addSet(form, excludes)}
                                  disabled={isReadOnly || isSubmitting}
                                />
                              </>
                            )}
                          </FieldArray>
                        </div>
                      )}
                    </div>

                    {/* Sidebar */}
                    <div className={`aud-v2-sidebar ${isGuideHidden ? 'aud-v2-sidebar--collapsed' : ''}`}>
                      <div className="aud-v2-sidebar__sticky">
                        <LogicGuide
                          isHidden={isGuideHidden}
                          onToggle={() => setIsGuideHidden(!isGuideHidden)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                {!isReadOnly && (
                  <div className="aud-v2-footer">
                    {/* Show validation errors */}
                    {form.errors && Object.keys(form.errors).length > 0 && form.submitCount > 0 && (
                      <div className="aud-v2-footer__errors">
                        Please fix the errors above before saving.
                      </div>
                    )}
                    <button
                      type="button"
                      className="aud-v2-btn aud-v2-btn--secondary"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Discard Changes
                    </button>
                    <button
                      type="submit"
                      className="aud-v2-btn aud-v2-btn--primary"
                      disabled={isSubmitting}
                      onClick={() => {
                        // Force validation and show any errors
                        form.validateForm().then((errors) => {
                          if (Object.keys(errors).length > 0) {
                            console.log('Validation errors:', errors);
                          }
                        });
                      }}
                    >
                      <span className="material-icons">save</span>
                      Save Audience
                    </button>
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default NewAudience;
